'use client';

import { useTranslations, useLocale } from 'next-intl';
import { UserPlus, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Link } from '@/i18n/routing';
import { PermissionGate } from '@/features/auth';
import { RoleColorBadge } from '@/features/roles';
import type { User } from '../types';
import { isSuperAdminUser } from '../utils';

interface UserCompactRowProps {
  user: User;
  onAssignRole: (user: User) => void;
  onToggleStatus: (user: User) => void;
}

function getRoleDisplayName(
  role: { name: string; name_en: string | null; name_ar: string | null },
  locale: string
): string {
  if (locale === 'ar' && role.name_ar) return role.name_ar;
  if (locale === 'en' && role.name_en) return role.name_en;
  return role.name_en || role.name_ar || role.name;
}

export function UserCompactRow({
  user,
  onAssignRole,
  onToggleStatus
}: UserCompactRowProps) {
  const _t = useTranslations('users');
  const locale = useLocale();

  const initials = user.name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const isDisabled = isSuperAdminUser(user) || user.status === 'locked';

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      <div className='bg-primary/10 text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold'>
        {initials}
      </div>

      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <span className='truncate font-medium'>{user.name}</span>
          <span className='text-muted-foreground hidden truncate text-sm sm:inline'>
            {user.email}
          </span>
        </div>
      </div>

      <div className='hidden flex-wrap gap-1 md:flex'>
        {user.roles.slice(0, 2).map((role) => (
          <RoleColorBadge
            key={role.name}
            name={getRoleDisplayName(role, locale)}
            color={role.color}
          />
        ))}
        {user.roles.length > 2 && (
          <span className='text-muted-foreground text-xs'>
            +{user.roles.length - 2}
          </span>
        )}
      </div>

      <Switch
        checked={user.status === 'active'}
        disabled={isDisabled}
        onCheckedChange={() => onToggleStatus(user)}
      />

      <div className='flex shrink-0 items-center gap-1'>
        {!isSuperAdminUser(user) && (
          <PermissionGate permissions={['roles.edit']}>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              onClick={() => onAssignRole(user)}
            >
              <UserPlus className='h-3.5 w-3.5' />
            </Button>
          </PermissionGate>
        )}
        <PermissionGate permissions={['users.edit']}>
          <Button variant='ghost' size='icon' className='h-7 w-7' asChild>
            <Link href={`/dashboard/users/${user.id}/edit`}>
              <Pencil className='h-3.5 w-3.5' />
            </Link>
          </Button>
        </PermissionGate>
      </div>
    </div>
  );
}
